import { CalendarDate } from "@uselessdev/datepicker";
import { CalendarValues } from "@uselessdev/datepicker/dist/declarations/types/src/types";
import { formatISO, parseISO } from "date-fns";
import React, { useCallback } from "react";
import { useController } from "react-hook-form";
import { usePrefixedFieldName } from "shared/components/form/FormFieldPrefix";
import { FormFieldProps } from "shared/components/form/FormFieldProps";
import { useIsFormDisabled } from "shared/components/form/FormStateProvider";
import {
  CalendarDateRange,
  CalendarView,
} from "shared/components/input/date/CalendarView";
import { getFieldError } from "shared/utils/FormUtils";

export interface CalendarInputProps {
  name?: string;
  value?: Date | CalendarDateRange;
  onChangeValue: (value: Date | undefined) => void;
  error?: string;
  isDisabled?: boolean;
}

export const CalendarInput = React.forwardRef<
  HTMLInputElement,
  CalendarInputProps
>(({ value, onChangeValue }) => {
  const onSelectDate = useCallback(
    (selectedValue: CalendarDate | CalendarValues) => {
      onChangeValue(selectedValue as Date);
    },
    [onChangeValue],
  );

  return <CalendarView onSelectDate={onSelectDate} value={value} />;
});
CalendarInput.displayName = "CalendarInput";

export type FormCalendarInputProps = FormFieldProps &
  Omit<CalendarInputProps, "name" | "onChangeValue" | "error">;

export const FormCalendarInput: React.FC<FormCalendarInputProps> = ({
  name,
  isDisabled,
  dependentFieldNames,
  ...otherProps
}) => {
  const prefixedName = usePrefixedFieldName(name);
  const {
    field: { onChange, value, ref, ...field },
    formState: { errors },
  } = useController<{ date?: string }>({
    name: prefixedName as "date",
    shouldUnregister: true,
    rules: {
      deps: dependentFieldNames,
    },
  });
  const isFormDisabled = useIsFormDisabled();
  return (
    <CalendarInput
      value={value ? parseISO(value) : undefined}
      onChangeValue={(value) => {
        onChange(
          value ? formatISO(value, { representation: "date" }) : undefined,
        );
      }}
      isDisabled={isDisabled || isFormDisabled}
      error={getFieldError(errors, prefixedName)?.message}
      ref={ref}
      {...field}
      {...otherProps}
    />
  );
};
